import type { Middleware } from 'openapi-fetch';

/** The cookie the backend writes the CSRF token to (readable by the page), and the header that sends it back. */
const XSRF_COOKIE = 'XSRF-TOKEN';
const XSRF_HEADER = 'X-XSRF-TOKEN';
const MUTATING_METHODS = new Set(['PUT', 'PATCH', 'POST', 'DELETE']);

let redirected = false;

/**
 * Sends the browser to the sign-in, which returns to the current page afterwards. At most once per page load: several
 * calls answering 401 at the same time must not start several navigations.
 */
export function signInRedirect(
  location: Pick<Location, 'pathname' | 'search' | 'assign'> = window.location,
): void {
  if (redirected) return;
  redirected = true;
  location.assign(`/api/login?returnTo=${encodeURIComponent(location.pathname + location.search)}`);
}

/** Lets a test start again from a page that has not redirected yet. */
export function resetSignInRedirectForTests(): void {
  redirected = false;
}

/** The CSRF token in the cookies, if the backend has set one. */
export function xsrfToken(cookie: string = document.cookie): string | undefined {
  for (const part of cookie.split(';')) {
    const separator = part.indexOf('=');
    if (separator > 0 && part.slice(0, separator).trim() === XSRF_COOKIE) {
      return decodeURIComponent(part.slice(separator + 1).trim());
    }
  }
  return undefined;
}

/** Sends the CSRF token with every change, and follows a 401 through the sign-in. */
export const sessionMiddleware: Middleware = {
  onRequest({ request }) {
    const token = xsrfToken();
    if (token !== undefined && MUTATING_METHODS.has(request.method.toUpperCase())) {
      request.headers.set(XSRF_HEADER, token);
    }
    return request;
  },
  onResponse({ response }) {
    if (response.status === 401) {
      signInRedirect();
    }
    return undefined;
  },
};
